/**
 * 播放介面的時間與順序計算，由 App 呼叫，不參與後端音源決策。
 * 將播放中的清單與正在瀏覽的分頁分開，避免切頁後跳到另一份清單。
 */
export function formatTime(seconds) {
  const value = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  return `${Math.floor(value / 60)}:${String(value % 60).padStart(2, "0")}`;
}

/** 依開始播放時的清單找相鄰歌曲；目前歌曲已移除時，從清單端點接續。 */
export function adjacentSong(songs, recordingId, direction) {
  if (!songs.length) return null;
  const index = songs.findIndex((song) => song.recordingId === recordingId);
  if (index < 0) return direction < 0 ? songs[songs.length - 1] : songs[0];
  return songs[(index + direction + songs.length) % songs.length];
}

export const PLAYBACK_MODES = Object.freeze(["list", "one", "shuffle"]);

/** 防止舊版或損壞的本機設定讓播放器落入未知模式。 */
export function normalizePlaybackMode(value) {
  return PLAYBACK_MODES.includes(value) ? value : "list";
}

/** 依「列表循環 → 單曲循環 → 隨機播放」順序切換。 */
export function nextPlaybackMode(value) {
  const index = PLAYBACK_MODES.indexOf(normalizePlaybackMode(value));
  return PLAYBACK_MODES[(index + 1) % PLAYBACK_MODES.length];
}

/**
 * 隨機模式以索引偏移避開目前歌曲，不建立候選陣列；其他模式維持相鄰順序。
 * recordingId 在播放清單中視為唯一值，與歌曲資料契約一致。
 */
export function songForPlaybackMode(songs, recordingId, direction, mode, random = Math.random) {
  if (normalizePlaybackMode(mode) !== "shuffle" || direction < 0 || songs.length < 2) {
    return adjacentSong(songs, recordingId, direction);
  }
  const sample = Number(random());
  const normalized = Number.isFinite(sample) ? Math.max(0, Math.min(sample, 0.999999999)) : 0;
  const currentIndex = songs.findIndex((song) => song.recordingId === recordingId);
  if (currentIndex < 0) return songs[Math.floor(normalized * songs.length)];
  const sampledIndex = Math.floor(normalized * (songs.length - 1));
  return songs[sampledIndex >= currentIndex ? sampledIndex + 1 : sampledIndex];
}

/** 預先建立的 Session 必須屬於同一首歌，且至少還有 15 秒可用時間。 */
export function preparedPlaybackIsUsable(prepared, song, now = Date.now()) {
  if (!prepared?.session || !prepared?.candidate || !song) return false;
  if (prepared.song?.recordingId !== song.recordingId) return false;
  if (prepared.song?.playbackRef !== song.playbackRef) return false;
  const expiresAt = Date.parse(
    prepared.session.expiresAt || prepared.session.decisionExpiresAt || ""
  );
  return Number.isFinite(expiresAt) && expiresAt > now + 15_000;
}

/** 將誤刪歌曲放回原位置；若歌曲已由其他操作加回，保持現有清單不重複。 */
export function restoreSongAt(songs, song, index) {
  if (!song || songs.some((item) => item.recordingId === song.recordingId)) return songs;
  const next = [...songs];
  const target = Math.max(0, Math.min(Number.isInteger(index) ? index : next.length, next.length));
  next.splice(target, 0, song);
  return next;
}
